/**
 * Each country's own calendar, as the browser's Intl knows it (the Calendars mockup): the year
 * and date there, the lunar day where months follow the moon, and the days of its weekend.
 */

/** A calendar a country keeps beside ours: its Intl name and how it is used. */
export interface LocalCalendar {
  /** Intl's name: `buddhist`, `chinese`, `islamic-umalqura`. */
  system: string;
  /** The country's own locale, its date written as there. */
  locale: string;
  /** Its months by the moon: each day said by its lunar day and month. */
  lunar: boolean;
  /** Kept for its feasts beside ours, not the civil one. */
  religious: boolean;
  /** Its months and days ours, its years its own: Thailand's, Japan's, Taiwan's. */
  ourMonths: boolean;
}

const civil = (system: string, locale: string): LocalCalendar => ({
  system,
  locale,
  lunar: false,
  religious: false,
  ourMonths: ['buddhist', 'japanese', 'roc'].includes(system),
});
const lunar = (system: string, locale: string): LocalCalendar => ({
  system,
  locale,
  lunar: true,
  religious: false,
  ourMonths: false,
});
const islamic = (locale: string): LocalCalendar => ({
  system: 'islamic-umalqura',
  locale,
  lunar: false,
  religious: true,
  ourMonths: false,
});

/** The calendars kept by country; a country on ours alone is not here. */
const CALENDARS: Record<string, LocalCalendar> = {
  TH: civil('buddhist', 'th-TH'),
  JP: civil('japanese', 'ja-JP'),
  TW: civil('roc', 'zh-TW'),
  IR: civil('persian', 'fa-IR'),
  AF: civil('persian', 'fa-AF'),
  ET: civil('ethiopic', 'am-ET'),
  IN: civil('indian', 'hi-IN'),
  IL: civil('hebrew', 'he-IL'),
  SA: civil('islamic-umalqura', 'ar-SA'),
  VN: lunar('chinese', 'vi-VN'),
  CN: lunar('chinese', 'zh-CN'),
  HK: lunar('chinese', 'zh-HK'),
  MO: lunar('chinese', 'zh-MO'),
  KR: lunar('dangi', 'ko-KR'),
  ...Object.fromEntries(
    [
      'AE',
      'QA',
      'KW',
      'BH',
      'OM',
      'YE',
      'JO',
      'EG',
      'MA',
      'DZ',
      'TN',
      'LY',
      'SD',
      'IQ',
      'SY',
    ].map((code) => [code, islamic(`ar-${code}`)]),
  ),
  MY: islamic('ms-MY'),
  ID: islamic('id-ID'),
  BN: islamic('ms-BN'),
  PK: islamic('ur-PK'),
  MV: islamic('dv-MV'),
};

/** A country's own calendar beside ours; `null` where ours is the only one. */
export function calendarOf(country: string): LocalCalendar | null {
  return CALENDARS[country] ?? null;
}

/** The weekend's days, Monday 1 to Sunday 7, where it is not Saturday and Sunday (CLDR). */
const WEEKENDS: Record<string, number[]> = {
  IR: [5],
  IN: [7],
  UG: [7],
  AF: [4, 5],
  ...Object.fromEntries(
    ['BH', 'DZ', 'EG', 'IL', 'IQ', 'JO', 'KW', 'LY', 'OM', 'QA', 'SA', 'SD', 'SY', 'YE'].map(
      (code) => [code, [5, 6]],
    ),
  ),
};

/** A country's weekend, Monday 1 to Sunday 7. */
export function weekendOf(country: string): number[] {
  return WEEKENDS[country] ?? [6, 7];
}

/** The day a week starts on, Monday 1 to Sunday 7, where it is not Monday (CLDR). */
const FIRST_DAYS: Record<string, number> = {
  MV: 5,
  ...Object.fromEntries(
    ['AF', 'BH', 'DZ', 'EG', 'IQ', 'IR', 'JO', 'KW', 'LY', 'OM', 'QA', 'SD', 'SY'].map(
      (code) => [code, 6],
    ),
  ),
  ...Object.fromEntries(
    ['ET', 'HK', 'ID', 'IL', 'IN', 'JP', 'KR', 'MO', 'PK', 'SA', 'TH', 'TW', 'YE'].map(
      (code) => [code, 7],
    ),
  ),
};

/** The day a country's week starts on, Monday 1 to Sunday 7: Thailand's Sunday. */
export function weekStartOf(country: string): number {
  return FIRST_DAYS[country] ?? 1;
}

/**
 * The day a calendar's week starts on where it is kept: the Hijri week from Sunday, its first day
 * by name; else as the country starts it.
 */
export function weekStartIn(own: LocalCalendar, country: string): number {
  return own.system.startsWith('islamic') ? 7 : weekStartOf(country);
}

const at = (day: string) => Date.parse(`${day}T00:00:00Z`);
const part = (parts: Intl.DateTimeFormatPart[], type: string) =>
  parts.find((one) => one.type === type)?.value ?? '';

/** A day in a calendar, in a locale: `24. november 2569 BK`, `24 พฤศจิกายน 2569`. */
export function dateIn(day: string, system: string, locale: string): string {
  return new Intl.DateTimeFormat(`${locale}-u-ca-${system}`, {
    dateStyle: 'long',
    timeZone: 'UTC',
  }).format(at(day));
}

/** A day's lunar month and day. */
export function lunarOf(day: string, system: string): { month: number; day: number } {
  const parts = new Intl.DateTimeFormat(`en-u-ca-${system}`, {
    month: 'numeric',
    day: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(at(day));
  return { month: Number(part(parts, 'month')), day: Number(part(parts, 'day')) };
}

/**
 * The animal of the lunar year a day falls in, by its place among the twelve, the Rat 0: 2026's
 * from its Tết, the Horse, 6.
 */
export function animalOf(day: string, system: string): number {
  const parts = new Intl.DateTimeFormat(`en-u-ca-${system}`, {
    year: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(at(day));
  const related = Number(part(parts, 'relatedYear'));
  return (((related - 4) % 12) + 12) % 12;
}

/** The year a day falls in by a calendar, as it numbers it: Thailand's 2569, Taiwan's 115. */
export function yearIn(day: string, system: string): string {
  const parts = new Intl.DateTimeFormat(`en-u-ca-${system}`, {
    year: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(at(day));
  return part(parts, 'year');
}

/** A day's day of the month by a calendar. */
export function localDay(day: string, system: string): number {
  return Number(
    part(
      new Intl.DateTimeFormat(`en-u-ca-${system}`, {
        day: 'numeric',
        timeZone: 'UTC',
      }).formatToParts(at(day)),
      'day',
    ),
  );
}

/** The month by a calendar that a day falls in: its first day and the first of the next. */
export function monthAround(day: string, system: string): { first: string; next: string } {
  const step = (from: string, by: number) =>
    new Date(at(from) + by * 86_400_000).toISOString().slice(0, 10);
  let first = day;
  while (localDay(first, system) !== 1) first = step(first, -1);
  let next = step(first, 1);
  while (localDay(next, system) !== 1) next = step(next, 1);
  return { first, next };
}

/** A month's name by a calendar, in a locale: `Jumada II 1448`, `tháng 10 năm Bính Ngọ`. */
export function monthName(day: string, system: string, locale: string): string {
  return new Intl.DateTimeFormat(`${locale}-u-ca-${system}`, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(at(day));
}

/** The months by a calendar from one day's to another's, each its first day and the next's. */
export function monthsAcross(
  from: string,
  to: string,
  system: string,
): { first: string; next: string }[] {
  let month = monthAround(from, system);
  const all = [month];
  while (month.next <= to) {
    month = monthAround(month.next, system);
    all.push(month);
  }
  return all;
}
